// Coach-mark tour content. Order matters — see /root/.claude/plans/jazzy-hopping-stream.md
// for the full design rationale (surfaces, includeUniversal gating, etc).
//
// `selector` and `placement` ({side, align}) can each be either a plain value (same on
// both surfaces) or a `{ mobile, desktop }` object for steps that need to target/place
// differently per surface — e.g. pointing at the bottom-nav button on mobile (where the
// tab's own content is too tall to be a reliable driver.js target) but highlighting the
// real content area on desktop (where it's height-bounded). Either key may be omitted,
// meaning "no element" on that surface → a centered floating popover, same as the
// welcome/closing steps.
//
// `nav` describes what useTour.js must do before this step can be highlighted. Route/pane
// are carried forward across steps that don't set their own (see effectiveNavs in
// useTour.js), so any step always re-selects the correct tab/pane regardless of which
// direction — or how many steps — we're jumping from:
//   - mobileRoute: router.replace() target, applied only when surface === 'mobile'
//   - desktopPane: { sidebarTab } | { statsPaneTab }, applied only when surface === 'desktop'
//   - simulateClick: CSS selector to .click() — only for targets that set EXPLICIT state
//     (opens a Teleported modal); don't use this on a toggle
//   - dispatchEvent: fires a bare `window` CustomEvent with this name — for state a
//     component sets directly in response (e.g. opening the user-menu dropdown, which
//     TOGGLES on a real click, so simulating a click can't reliably know which way it'll go)
//   - keepModalsOpen: this step's own simulateClick depends on a modal the PRECEDING
//     step's nav opens — reapplied recursively if we jump straight to this step from
//     anywhere else (see applyStepNav in useTour.js)
//
// `emphasize` adds a pulsing blue glow ring around the MAIN target, in addition to
// driver.js's own spotlight — for thin drag-handle targets (or a small nav-bar button
// standing in for the real target) the default cutout alone is easy to miss. Accepts
// `true` (both surfaces), or `'mobile'` / `'desktop'` to scope it to one.
//
// `ringSelector` (plain string, `{ mobile, desktop }`, or an array of either) adds the
// SAME blue ring to a DIFFERENT element than the main target — e.g. driver.js highlights
// the real content area on desktop while a secondary ring also circles the small
// tab-selector button used to reach it. An array gets one independent ring per entry
// (see the closing step: one ring around the whole dropdown, another around its trigger).
//
// `advanceOnClick` (a selector, resolved via `bySurface` same as `selector`) lets the
// REAL element — not just the popover's own Next button — advance the tour when the
// user clicks it for real. Only meaningful on a step whose main target is itself
// genuinely clickable (i.e. also has `lowDim` — see lesson-edit-button below).

export const desktopOnlySteps = [
  {
    id: 'desktop-resize-horizontal',
    // Main target IS the small grip graphic, not the whole draggable strip — so driver.js's
    // own spotlight cutout matches the blue ring exactly instead of covering a much larger
    // area than what's actually outlined.
    selector: '[data-tour="resize-grip-horizontal"]',
    emphasize: true,
    // Same reasoning as calendar-zoom-handle/calendar-create-lesson in universalSteps
    // below — this step invites a real drag, and the whole point is watching the panes
    // actually resize, so the normal dimming (which doesn't track the moving divider)
    // just looks broken once the user starts dragging. See lowDim in useTour.js.
    lowDim: true,
    title: 'Resize the panes',
    description: 'Drag this handle to make the left pane wider or narrower.',
    placement: { side: 'left', align: 'center' },
  },
  {
    id: 'desktop-resize-vertical',
    selector: '[data-tour="resize-grip-vertical"]',
    emphasize: true,
    lowDim: true, // see the comment on desktop-resize-horizontal above
    title: 'Resize top and bottom',
    description: 'Drag this handle to adjust how much space the Calendar gets versus the tabs below it.',
    placement: { side: 'bottom', align: 'center' },
  },
]

// Bump whenever a step is added or materially changed — compared against a
// localStorage-persisted "last seen" value in App.vue to decide whether a user who
// already completed the tour should get the "what's new" nudge (buildWhatsNewStep)
// instead of staying silent forever. Not tied to the welcome/first-run gating below,
// which is driven by the backend's tour_*_completed_at flags instead.
export const TOUR_VERSION = 3

export const universalSteps = [
  {
    id: 'attendance-appearance',
    // Mobile: points at the bottom-nav button instead of the tab's own content, which
    // isn't height-bounded there (unlike desktop's `contained` mode) — see the emphasis-
    // ring/overlay-opacity design notes in useTour.js for why. Desktop: highlights the
    // real (properly bounded) column directly.
    selector: { mobile: '[data-tour="nav-attendance"]', desktop: '[data-tour="attendance-list"]' },
    emphasize: 'mobile',
    nav: { mobileRoute: '/', desktopPane: { sidebarTab: 'attendance' } },
    title: 'Attendance',
    description: 'Lessons missing attendance appear in red, oldest first — mark one and it scrolls you to the next.',
    placement: { mobile: { side: 'top', align: 'center' }, desktop: { side: 'left', align: 'start' } },
  },
  {
    id: 'attendance-marking-window',
    // No mobile selector on purpose — centered/floating there, same as before.
    selector: { desktop: '[data-tour="attendance-list"]' },
    title: 'When you can mark attendance',
    description: 'You can mark a lesson absent/attended up to 24 hours in advance. Beyond that window, lessons can only be marked excused.',
    placement: { desktop: { side: 'left', align: 'start' } },
  },
  {
    id: 'lesson-detail-popup',
    selector: '[data-tour="lesson-details-modal"]',
    // Explicit mobileRoute even though attendance-appearance (right before this in the
    // full tour) already carries '/' forward — the what's-new mini-tour (see
    // whatsNewV3Steps below) starts ITS OWN sequence here, with no earlier step to carry
    // a route forward from, so without this a mobile user landing on a different tab
    // would have no lesson-title to click and the simulated click would silently no-op.
    // A no-op for the full tour (already '/' by the time we get here either way).
    nav: { mobileRoute: '/', simulateClick: '[data-tour="lesson-title"]' },
    title: 'Lesson details',
    description: "Tap a lesson's title to see its full details.",
    placement: { side: 'top', align: 'center' },
  },
  {
    id: 'lesson-edit-button',
    // Depends on lesson-detail-popup's details modal still being open — no click of its
    // own needed (the Edit button is already visible inside it), just keepModalsOpen so
    // closeLessonModals() doesn't tear the details modal down on the way in, and so a
    // direct jump here replays lesson-detail-popup's own nav first (see applyStepNav).
    selector: '[data-tour="lesson-details-edit-btn"]',
    nav: { keepModalsOpen: true },
    emphasize: true,
    // The Edit button is the step's real, live target — clicking it for real opens the
    // same Edit/Delete/Clone modal the next step is about, so let that click ALSO
    // advance the tour instead of forcing a separate press of the popover's Next.
    // lowDim, same reasoning as calendar-zoom-handle/calendar-create-lesson: the modal
    // that opens on click shouldn't look half-darkened under the normal overlay.
    advanceOnClick: '[data-tour="lesson-details-edit-btn"]',
    lowDim: true,
    title: 'Edit this lesson',
    description: 'Click here to edit, delete, or clone a lesson.',
    placement: { side: 'top', align: 'center' },
  },
  {
    id: 'lesson-edit-scopes',
    // Highlights the WHOLE popup rather than just the scope toggle — the redesigned
    // modal now leads with a separate Edit/Delete/Clone action row above the scope
    // toggle, so a single blurb covering the whole thing reads better than a bulleted
    // breakdown of just one sub-control.
    selector: '[data-tour="lesson-edit-modal"]',
    nav: { simulateClick: '[data-tour="lesson-details-edit-btn"]', keepModalsOpen: true },
    title: 'Editing a lesson',
    description: 'In this menu you can edit, delete, or clone (make copy) of individual lessons, or edit / end the lesson series.',
    placement: { side: 'top', align: 'center' },
  },
  {
    id: 'calendar-tab',
    // Mobile: point at the nav button (Calendar is always visible on desktop, no tab
    // switch needed there, so desktop keeps highlighting the real grid directly).
    selector: { mobile: '[data-tour="nav-my-schedule"]', desktop: '[data-tour="calendar-grid"]' },
    emphasize: 'mobile',
    nav: { mobileRoute: '/my-schedule' },
    title: 'Calendar',
    description: 'This is another view of the same schedule — tap any lesson here to see the same details popup.',
    placement: { mobile: { side: 'top', align: 'center' }, desktop: { side: 'top', align: 'center' } },
  },
  {
    id: 'calendar-zoom-handle',
    selector: '[data-tour="hour-height-handle"]',
    emphasize: true,
    // These two steps are the only ones where the user is actually invited to interact
    // with the real app (drag to resize, tap to open a real modal) rather than just look
    // at a spotlighted area — the normal desktop overlay dimming (0.6 opacity) looks fine
    // sitting still, but reads as broken once the content the user is actively dragging
    // or the modal they just opened is sitting half-darkened underneath it. Dropping the
    // dim to near-zero for just these two (see lowDim in useTour.js) keeps the blue ring
    // as the "look here" cue without fighting the interaction it's inviting.
    lowDim: true,
    title: 'Zoom the schedule',
    description: 'Drag here to make the time grid taller or shorter.',
    placement: { side: 'right', align: 'center' },
  },
  {
    id: 'calendar-create-lesson',
    // Targets a synthetic, lesson-sized marker (see calendarSlotEl in useTour.js) rather
    // than the whole scrollable viewport — a small, roughly-lesson-sized area reads as
    // "here's an example empty spot" instead of spotlighting the entire calendar. The
    // marker is pointer-events:none, so a real tap on it falls through to the actual
    // grid underneath and opens the real create-lesson modal (its Create button is
    // disabled for the duration of the tour — see LessonCreateModal.vue).
    selector: '[data-tour="calendar-empty-slot"]',
    emphasize: true,
    lowDim: true, // see the comment on calendar-zoom-handle above
    // Real interaction here is capped at exactly one "open the chooser, then pick an
    // option" cycle — see armUntilFirstClick in useTour.js. Without this, the calendar
    // stays fully clickable for the whole step, so a user could keep opening new
    // choosers (or the real create-lesson modal) from other empty spots, stacking real
    // app state up behind the coachmark with no way back to just the tour (confirmed:
    // this is what "clicking behaves strangely" turned out to be).
    armUntilFirstClick: true,
    title: 'Add a lesson',
    description: 'Tap an empty spot on the calendar to add a new lesson or block off time as unavailable, feel free to try it, nothing will actually be created while the tour is open.',
    placement: { side: 'bottom', align: 'center' },
  },
  {
    id: 'students-tab',
    // Mobile: nav button only. Desktop: highlights the whole stats pane (tab bar +
    // content together, not just the content below it) so the ring around the tab
    // selector sits inside the lit-up area instead of the darkened surrounding overlay.
    selector: { mobile: '[data-tour="nav-students"]', desktop: '[data-tour="stats-pane"]' },
    emphasize: 'mobile',
    ringSelector: { desktop: '[data-tour="stats-tab-students"]' },
    nav: { mobileRoute: '/students', desktopPane: { statsPaneTab: 'students' } },
    title: 'Students',
    description: 'See all your students, past and present, along with their contact info.',
    placement: { mobile: { side: 'top', align: 'center' }, desktop: { side: 'bottom', align: 'start' } },
  },
  {
    id: 'invoices-tab',
    selector: { mobile: '[data-tour="nav-month-stats"]', desktop: '[data-tour="stats-pane"]' },
    emphasize: 'mobile',
    ringSelector: { desktop: '[data-tour="stats-tab-invoices"]' },
    nav: { mobileRoute: '/stats', desktopPane: { statsPaneTab: 'invoices' } },
    title: 'Invoices',
    description: 'Review and confirm past and upcoming invoices and payments. Tap a pay period to expand its lessons.',
    placement: { mobile: { side: 'top', align: 'center' }, desktop: { side: 'bottom', align: 'start' } },
  },
]

// Shown after desktopOnlySteps on desktop (right before closing), but immediately after
// universalSteps on mobile (there's nothing desktop-only to come after there) — see
// buildSteps in useTour.js for where these get spliced in.
export const accountSteps = [
  {
    id: 'theme-toggle',
    selector: '[data-tour="theme-toggle"]',
    emphasize: true,
    title: 'Light / dark mode',
    description: 'Switch anytime — your choice is remembered.',
    placement: { side: 'bottom', align: 'end' },
  },
  {
    id: 'actions-menu',
    // Expanded, not collapsed — main target is the wrapper (button + open dropdown
    // together, see HeaderControls.vue) so driver.js's own spotlight covers the whole
    // menu, while a secondary ring circles JUST the trigger button. The menu's own
    // contents (New Lesson, Mark Time Off, etc) are consumer-supplied via
    // HeaderControls' `action-items` slot, so there's no single fixed item to point at.
    selector: '[data-tour="actions-menu-wrapper"]',
    ringSelector: '[data-tour="actions-menu-trigger"]',
    nav: { dispatchEvent: 'tour:open-actions-menu' },
    title: 'Actions Menu',
    description: 'Create lessons, Mark Time Off, and other actions will be available here.',
    placement: { side: 'bottom', align: 'end' },
  },
  {
    id: 'user-menu-intro',
    // Same expanded-wrapper + button-ring idiom as actions-menu above.
    selector: '[data-tour="user-menu-wrapper"]',
    ringSelector: '[data-tour="user-menu-trigger"]',
    nav: { dispatchEvent: 'tour:open-user-menu' },
    title: 'Your account menu',
    description: 'Edit your availability, profile, and preferences here.',
    placement: { side: 'bottom', align: 'end' },
  },
]

// A dedicated, short "what's new" tour for TOUR_VERSION 3 (see buildWhatsNewStep +
// useTour.js's whatsNew branch) — shown to a user who already completed the full tour
// before this version shipped, instead of replaying the whole thing end to end. These
// are standalone objects with their own announcement-style copy, NOT the same step
// objects the full tour uses (those keep their normal second-person instructional
// copy) — a couple of ids are deliberately reused (calendar-create-lesson,
// actions-menu) purely so they pick up existing id-keyed special-case behavior in
// useTour.js (the calendar-empty-slot marker tracking), not because the content matches.
//
// NOTE for whoever adds the next version's steps: this list is manually curated, not
// auto-diffed off the real step definitions — bump TOUR_VERSION above and build a new
// whatsNewV4Steps (or similar) the same way.
export const whatsNewV3Steps = [
  {
    id: 'calendar-create-lesson',
    selector: '[data-tour="calendar-empty-slot"]',
    emphasize: true,
    lowDim: true,
    armUntilFirstClick: true, // see the comment on this same id in universalSteps above
    nav: { mobileRoute: '/my-schedule' },
    title: 'New: Create or Block Time',
    description: "Clicking an empty spot on the calendar now allows you to create a new lesson or block off time as Unavailable.",
    placement: { side: 'bottom', align: 'center' },
  },
  {
    id: 'actions-menu',
    selector: '[data-tour="actions-menu-wrapper"]',
    ringSelector: '[data-tour="actions-menu-trigger"]',
    nav: { dispatchEvent: 'tour:open-actions-menu' },
    title: 'Actions Menu',
    description: 'Find common actions here.',
    placement: { side: 'bottom', align: 'end' },
  },
  {
    id: 'whats-new-availability',
    // Self-sufficient: expands the Availability editor (tourExpandAvailability in
    // App.vue, via EditProfileModal's startExpanded prop — see its own comment for why
    // this can't just be a simulated click), opens the user menu, and clicks Edit
    // Profile — all in one nav (see useTour.js's dispatchEvent-then-simulateClick
    // ordering) rather than depending on preceding steps to set each of these up.
    selector: '[data-tour="availability-section"]',
    ringSelector: '[data-tour="availability-add-block"]',
    nav: {
      dispatchEvent: ['tour:expand-availability', 'tour:open-user-menu'],
      simulateClick: '[data-tour="edit-profile-menu-item"]',
    },
    title: 'Set Availability',
    description: "Now allows multiple blocks per day, letting you create gaps in your day that won't be offered to students.",
    placement: { side: 'right', align: 'center' },
  },
  {
    id: 'whats-new-preferences',
    selector: '[data-tour="preferences-modal"]',
    nav: { dispatchEvent: 'tour:open-user-menu', simulateClick: '[data-tour="preferences-menu-item"]' },
    title: 'Preferences Menu',
    description: 'Find cool, teacher requested features here, take a look! More to come.',
    placement: { side: 'left', align: 'center' },
  },
  {
    id: 'whats-new-closing',
    // No selector — floating centered popover, same idiom as the welcome/whatsNew
    // intro slides, closing the previous step's modal (via closeLessonModals in
    // applyStepNav) on the way in.
    title: "That's it for now!",
    description: 'Thank you all for embracing the new software!',
  },
]

export function buildWelcomeStep(firstName) {
  return {
    popover: {
      title: firstName ? `Welcome, ${firstName}!` : 'Welcome!',
      // No step count here — driver.js's own `showProgress` indicator already shows it,
      // and leading with "N steps" reads as a chore rather than a benefit.
      description: 'A quick tour, replay any time!',
      // Nothing to go "back" to from the first slide.
      showButtons: ['next', 'close'],
    },
  }
}

// Shown instead of buildWelcomeStep when the user already completed the tour on this
// surface (tour_*_completed_at is set) but TOUR_VERSION has advanced since — a lighter
// "here's what's new" nudge rather than a first-timer's Welcome. Same steps play after
// it as a normal replay; only the intro slide's copy differs.
export function buildWhatsNewStep(firstName) {
  return {
    popover: {
      title: firstName ? `Welcome back, ${firstName}!` : 'Welcome back!',
      description: "We've rolled out a few new features, continue to just see what's new.",
      showButtons: ['next', 'close'],
    },
  }
}

export function buildClosingStep() {
  // Same shape as any other content step (not a raw driver-step object) — it needs a
  // `nav` (opening the dropdown) applied through the normal onNextClick pipeline before
  // it can highlight the Help item inside it. Now that useTour.js waits for the target
  // to actually exist before ever calling moveNext() (see waitForSelectorReady), pointing
  // the main highlight straight at Help is safe — driver.js is never asked to navigate
  // here until the dropdown is genuinely open.
  //
  // Uses `dispatchEvent` (sets dropdownOpen = true directly in HeaderControls.vue), not
  // `simulateClick` on the trigger — that button TOGGLES open/closed, so a simulated
  // click has no reliable way to know whether it's turning the menu on or off.
  return {
    id: 'closing',
    // Main target stays Help — driver.js's own spotlight cutout and the popover's
    // placement both anchor there. The ring only circles the collapsed trigger button
    // (what the user would actually click to reopen the menu later) — no separate ring
    // around the whole dropdown, that read as too busy alongside Help's own spotlight.
    selector: '[data-tour="help-menu-item"]',
    ringSelector: '[data-tour="user-menu-trigger"]',
    nav: { dispatchEvent: 'tour:open-user-menu' },
    title: "That's it!",
    description: 'You can replay this tour anytime from Help, right here.',
    placement: { side: 'left', align: 'center' },
  }
}
